/**
 * Client per le chiamate all'API Gateway backend.
 * Tutte le richieste includono il JWT dell'utente autenticato nell'header Authorization.
 */

import { DeleteObjectCommand, GetObjectCommand, HeadObjectCommand, PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { credentialProvider, region } from "./aws";
import type { Expense, Mileage } from "../types/presence";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

// Derive stage from the DynamoDB table name (e.g. "presenze-data-dev" → "dev")
const stage = (import.meta.env.VITE_PRESENCE_TABLE_NAME as string)
  .split("-")
  .pop() as string;
// presenze-data-dev-attachments presenze-data-prod-attachments ubisive-presenze-data-prod-attachments
// const attachmentsBucket = `presenze-data-${stage}-attachments`; 
const attachmentsBucket = (import.meta.env.VITE_ATTACHMENTS_BUCKET as string) || `presenze-data-${stage}-attachments`; 

const s3Client = new S3Client({
  region,
  credentials: credentialProvider,
  requestChecksumCalculation: "WHEN_REQUIRED",
  responseChecksumValidation: "WHEN_REQUIRED",
});

export type PresignedUploadResult = {
  url: string;
  key: string;
};

/**
 * Genera direttamente in frontend una presigned URL per il PUT su S3.
 * @returns url - URL firmata per il PUT diretto su S3
 * @returns key - chiave S3 dell'oggetto da usare come riferimento nella nota spese
 */
export async function getPresignedUploadUrl(params: {
  year: number;
  month: number;
  filename: string;
  contentType: string;
}): Promise<PresignedUploadResult> {
  const { year, month, filename, contentType } = params;
  const key = `${year}/${String(month).padStart(2, "0")}/${Date.now()}-${filename}`;
  const command = new PutObjectCommand({
    Bucket: attachmentsBucket,
    Key: key,
    ContentType: contentType,
  });
  const url = await getSignedUrl(s3Client, command, { expiresIn: 300 });
  return { url, key };
}

/**
 * Carica un file direttamente su S3 usando la presigned URL.
 */
export async function uploadFileToS3(url: string, file: File): Promise<void> {
  const response = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!response.ok) {
    throw new Error("Errore durante il caricamento del file su S3");
  }
}

/**
 * Elimina un oggetto da S3 usando le credenziali Cognito.
 */
export async function deleteObjectFromS3(key: string): Promise<void> {
  await s3Client.send(
    new DeleteObjectCommand({ Bucket: attachmentsBucket, Key: key }),
  );
}

/**
 * Genera una presigned URL per il download (GET) di un oggetto S3.
 */
export async function getPresignedDownloadUrl(key: string): Promise<string> {
  const command = new GetObjectCommand({ Bucket: attachmentsBucket, Key: key });
  return getSignedUrl(s3Client, command, { expiresIn: 300 });
}

/**
 * Verifica se l'immagine firma dell'utente esiste in S3.
 */
export async function checkSignatureExists(userId: string): Promise<boolean> {
  try {
    await s3Client.send(new HeadObjectCommand({
      Bucket: attachmentsBucket,
      Key: `signatures/${userId}.png`,
    }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Genera una presigned URL per il caricamento (PUT) della firma utente.
 * Il file viene sempre salvato con chiave fissa `signatures/{userId}.png`.
 */
export async function getPresignedSignatureUploadUrl(userId: string): Promise<string> {
  const command = new PutObjectCommand({
    Bucket: attachmentsBucket,
    Key: `signatures/${userId}.png`,
    ContentType: "image/png",
  });
  return getSignedUrl(s3Client, command, { expiresIn: 300 });
}

/**
 * Genera una presigned URL per visualizzare l'anteprima della firma.
 */
export async function getSignaturePreviewUrl(userId: string): Promise<string> {
  return getPresignedDownloadUrl(`signatures/${userId}.png`);
}

/**
 * Chiama la Lambda backend per inviare la mail delle presenze del mese.
 * La Lambda recupera gli allegati da S3 e li include nella mail.
 */
export async function sendPresenceEmail(params: {
  monthSk: string;
  test: boolean;
  userEmail: string;
  userName: string;
  idToken: string;
}): Promise<void> {
  const { idToken, ...body } = params;
  const response = await fetch(`${API_URL}/send-email`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? "Errore durante l'invio della mail");
  }
}

/**
 * Chiama la Lambda backend per generare il file xlsx della nota spese del mese
 * e lo scarica automaticamente nel browser.
 */
export async function generateExpenseReport(params: {
  monthSk: string;
  userName: string;
  idToken: string;
  expenses?: Expense[];
  mileages?: Mileage[];
}): Promise<void> {
  const { idToken, ...body } = params;
  const response = await fetch(`${API_URL}/generate-expense-report`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? "Errore durante la generazione del file");
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const yearMonth = body.monthSk.replace("presenceMonth#", "");
  a.download = `${body.userName.replace(/\s+/g, "_")}_${yearMonth}_nota_spese.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
